import { useMemo, useState } from "react";
import { Icon } from "./icons";
import { SectionHead } from "./SectionHead";
import { toast } from "./toast";
import {
  useCreateAssignment, useDeleteAssignment, useMembers,
  usePatchAssignment, useProjectAssignments,
} from "../lib/proofers";
import { useWorkflowBoard } from "../lib/workflowBoard";
import { avatarColor, initials } from "../lib/avatar";
import type { TrackFile } from "../lib/tracking";
import type { Assignment, Member } from "../api/types";

// Same words the proofer sees in My Work, so the board and their log agree.
const STATUS_LABEL: Record<string, string> = {
  assigned: "To do", in_progress: "In progress", submitted: "Complete",
  delivered: "Delivered", invoiced: "Invoiced", settled: "Settled",
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const hms = (s: number) => `${Math.floor(s / 3600)}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(Math.floor(s % 60))}`;
const mmss = (s: number) => `${Math.floor(s / 60)}:${pad2(Math.floor(s % 60))}`;

/** [1,2,3,5,6] -> "1–3, 5–6" — the same compression the server applies when it
 *  snapshots the scope label, so the preview here matches what gets stored. */
function compress(nums: number[]): string {
  if (!nums.length) return "";
  const o = [...new Set(nums)].sort((a, b) => a - b);
  const parts: [number, number][] = [];
  let start = o[0], prev = o[0];
  for (let i = 1; i < o.length; i++) {
    if (o[i] === prev + 1) { prev = o[i]; continue; }
    parts.push([start, prev]);
    start = prev = o[i];
  }
  parts.push([start, prev]);
  return parts.map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`)).join(", ");
}

/**
 * Assigning a proofer — the WHOLE interaction, used identically on the board's
 * Proofer cell (in a popover) and inside the Tracking View. One mechanism, one
 * set of habits to learn.
 *
 * The scope selection is the panel's OWN state, deliberately not the Tracking
 * View's chapter ticks. Those ticks mean "this file is part of the job" and
 * drive the project's runtime total; borrowing them as the assignment scope
 * made splitting a book between two proofers mutate the runtime twice and leave
 * ticks describing neither assignment. Here they only decide what's assignable
 * at all — an unticked file is out of the job, so it's shown but not pickable.
 */
export function AssignProoferPanel({ projectId, files, na, onDone }: {
  projectId: string; files: TrackFile[];
  /** "Proofing isn't needed here" — the same NA the Tech reviewer column has,
   *  stored the same way (`workflow.proofer === "NA"`). Mutually exclusive with
   *  a real assignment, so assigning someone clears it. */
  na?: { value: boolean; set: (on: boolean) => void };
  onDone?: () => void;
}) {
  const { data: rosterData } = useMembers();
  const { data: assignments } = useProjectAssignments(projectId);
  const create = useCreateAssignment();
  const patch = usePatchAssignment();
  const del = useDeleteAssignment();

  const [scope, setScope] = useState<"all" | "subset">("all");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [reassignFor, setReassignFor] = useState<string | null>(null);

  // Only people who actually proof are assignable — an admin-only teammate
  // shouldn't show up here. (An admin who opted to "also proof" holds the
  // proofer role, so they do.)
  const roster = useMemo(
    () => (rosterData?.members || []).filter((m) => m.status !== "disabled" && m.roles.includes("proofer")),
    [rosterData],
  );

  const rows = assignments || [];
  const inJob = useMemo(() => files.filter((f) => f.selected), [files]);
  const inJobSecs = inJob.reduce((a, f) => a + f.duration_seconds, 0);
  const pickedSecs = files.reduce((a, f) => a + (picked.has(f.chapter_id) ? f.duration_seconds : 0), 0);

  // Who already holds each file. A whole-project assignment holds everything in
  // the job; a subset holds exactly its listed files.
  const holders = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of rows) {
      const ids = a.scope === "all" ? inJob.map((f) => f.chapter_id) : a.scope_files;
      for (const id of ids) m.set(id, [...(m.get(id) || []), a]);
    }
    return m;
  }, [rows, inJob]);

  const unassigned = useMemo(
    () => inJob.filter((f) => !holders.has(f.chapter_id)).map((f) => f.chapter_id),
    [inJob, holders],
  );

  // The label the SERVER will snapshot: 1-based position in the full file list
  // (`_scope_summary`), not the filename's own number. Deriving it the same way
  // keeps this preview honest about what ends up on the assignment.
  const previewLabel = useMemo(() => {
    if (scope === "all") return "whole project";
    const pos = files.map((f, i) => (picked.has(f.chapter_id) ? i + 1 : 0)).filter(Boolean);
    return pos.length ? `files ${compress(pos)}` : "";
  }, [scope, picked, files]);

  // Picking files someone else already holds is allowed — a second pass over the
  // same audio is real work — but it should be a decision, not an accident.
  const clash = useMemo(() => {
    if (scope !== "subset") return null;
    const names = new Set<string>();
    let n = 0;
    picked.forEach((id) => {
      const hs = holders.get(id);
      if (hs?.length) { n++; hs.forEach((a) => a.proofer_name && names.add(a.proofer_name)); }
    });
    return n ? { n, who: [...names].join(", ") } : null;
  }, [scope, picked, holders]);

  const reset = () => { setScope("all"); setPicked(new Set()); setAnchor(null); };

  const toggle = (idx: number, f: TrackFile, shift: boolean) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (shift && anchor != null) {
        const [lo, hi] = anchor < idx ? [anchor, idx] : [idx, anchor];
        for (let i = lo; i <= hi; i++) if (files[i].selected) next.add(files[i].chapter_id);
      } else {
        next.has(f.chapter_id) ? next.delete(f.chapter_id) : next.add(f.chapter_id);
        setAnchor(idx);
      }
      return next;
    });
  };

  const assign = (memberId: string) => {
    const body = scope === "subset"
      ? { member_id: memberId, scope: "subset" as const, scope_files: [...picked] }
      : { member_id: memberId, scope: "all" as const };
    create.mutate({ projectId, body }, {
      onSuccess: () => {
        if (na?.value) na.set(false);   // a real proofer outranks "not required"
        toast("Proofer assigned", "success"); reset(); onDone?.();
      },
      onError: (e: unknown) => toast((e as Error)?.message || "Couldn't assign", "error"),
    });
  };

  const reassign = (assignmentId: string, memberId: string) => {
    patch.mutate({ id: assignmentId, patch: { member_id: memberId } }, {
      onSuccess: () => { toast("Reassigned", "success"); setReassignFor(null); },
      onError: () => toast("Couldn't reassign", "error"),
    });
  };

  const unassign = (a: Assignment) => {
    del.mutate(a.id, {
      onSuccess: () => toast(`Unassigned ${a.proofer_name ?? "proofer"}`, "success"),
      onError: () => toast("Couldn't unassign", "error"),
    });
  };

  if (!roster.length) {
    return <div className="ap-sec"><a className="ap-empty" href="/proofers">Invite a proofer first →</a></div>;
  }

  const blocked = scope === "subset" && picked.size === 0;

  return (
    <>
      {na?.value && rows.length === 0 && (
        <div className="ap-sec ap-nabar">
          <span className="ap-av sm empty">–</span>
          <span className="ap-nabar-t">
            <b>Not required</b>
            <span>No proofer needed for this project.</span>
          </span>
          <button className="ap-undo" onClick={() => na.set(false)}>Undo</button>
        </div>
      )}

      {rows.length > 0 && (
        <div className="ap-sec">
          <div className="ap-lbl">Assigned now</div>
          {rows.map((a) => (
            <div key={a.id} className="ap-arow">
              <span className="ap-av sm" style={{ background: avatarColor(a.proofer_name || "?") }}>
                {initials(a.proofer_name || "?")}
              </span>
              <span className="ap-who">
                <span className="ap-nm">{a.proofer_name}</span>
                <span className="ap-sc">
                  {a.scope_label || "whole project"}
                  {a.runtime_seconds ? ` · ${hms(a.runtime_seconds)}` : ""}
                </span>
              </span>
              <span className={`ap-st s-${a.status}`}>{STATUS_LABEL[a.status] ?? a.status}</span>
              <span className="ap-acts">
                <button className="ap-ic" title="Reassign to another proofer"
                  onClick={() => setReassignFor(reassignFor === a.id ? null : a.id)}>
                  <Icon name="edit" size={12} />
                </button>
                <button className="ap-ic danger" title={`Unassign ${a.proofer_name ?? ""}`} onClick={() => unassign(a)}>
                  <Icon name="x" size={13} />
                </button>
                {reassignFor === a.id && (
                  <RosterMenu roster={roster.filter((m) => m.id !== a.member_id)}
                    onPick={(mid) => reassign(a.id, mid)} />
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="ap-sec">
        <div className="ap-lbl">Scope</div>
        <div className="ap-scopes">
          <button className="ap-scope" aria-pressed={scope === "all"} onClick={() => setScope("all")}>
            <span className="r"><span className="dot" />Whole project</span>
            <span className="meta">{inJob.length} files · {hms(inJobSecs)}</span>
          </button>
          <button className="ap-scope" aria-pressed={scope === "subset"} onClick={() => setScope("subset")}>
            <span className="r"><span className="dot" />Selected files</span>
            <span className="meta">
              {picked.size ? `${picked.size} of ${inJob.length} · ${hms(pickedSecs)}` : "none picked yet"}
            </span>
          </button>
        </div>

        {scope === "subset" && (
          <div className="ap-picker">
            <div className="ap-pkbar">
              {unassigned.length > 0 && (
                <button className="ap-chip hero" title="Select every file no one holds yet"
                  onClick={() => { setPicked(new Set(unassigned)); setAnchor(null); }}>
                  Unassigned ({unassigned.length})
                </button>
              )}
              <button className="ap-chip" onClick={() => { setPicked(new Set(inJob.map((f) => f.chapter_id))); setAnchor(null); }}>All</button>
              <button className="ap-chip" onClick={() => { setPicked(new Set()); setAnchor(null); }}>None</button>
              <span className="ap-cnt">{picked.size} picked</span>
            </div>
            <div className="ap-pklist">
              {files.map((f, i) => {
                const held = holders.get(f.chapter_id);
                // The file's name, never chapter_name: names are heading-derived
                // and go stale, the filename is what everyone actually refers to
                // (as the project knows it - a corrected delivery has no "(v2)").
                const label = f.shown_filename || f.audio_filename || f.name;
                const num = f.seq ?? i + 1;
                return (
                  <div key={f.chapter_id}
                    className={`ap-fr${picked.has(f.chapter_id) ? " on" : ""}${f.selected ? "" : " excl"}${held ? " taken" : ""}`}
                    title={f.selected
                      ? `${label} · ${mmss(f.duration_seconds)}${held ? ` · already with ${held.map((a) => a.proofer_name).join(", ")}` : ""}`
                      : "Excluded from this job in the Chapters matrix — tick it there to make it assignable"}
                    onClick={f.selected ? (e) => toggle(i, f, e.shiftKey) : undefined}>
                    <span className="box" />
                    <span className="seq">{String(num).padStart(3, "0")}</span>
                    <span className="fn">{label}</span>
                    {held && (
                      <span className="ap-av xs" style={{ background: avatarColor(held[0].proofer_name || "?") }}>
                        {initials(held[0].proofer_name || "?")}
                      </span>
                    )}
                    {!f.selected && <span className="xtag">excluded</span>}
                    <span className="du">{mmss(f.duration_seconds)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {clash && (
          <div className="ap-warn">
            <Icon name="alert" size={12} />
            <span>
              {clash.n} {clash.n === 1 ? "file is" : "files are"} already with {clash.who} — assigning
              creates a second pass over {clash.n === 1 ? "it" : "them"}.
            </span>
          </div>
        )}
      </div>

      <div className="ap-sec">
        <div className="ap-lbl">
          Proofer
          <span className="sp">{blocked ? "pick files first" : `gets ${previewLabel}`}</span>
        </div>
        <div className="ap-roster">
          {roster.map((m) => {
            const mine = rows.filter((a) => a.member_id === m.id);
            return (
              <button key={m.id} className="ap-rbtn" disabled={blocked || create.isPending} onClick={() => assign(m.id)}>
                <span className="ap-av sm" style={{ background: avatarColor(m.name || m.email) }}>
                  {initials(m.name || m.email)}
                </span>
                <span className="ap-rnm">{m.name || m.email}</span>
                {mine.length > 0 && <span className="ap-has">has {mine[0].scope_label || "whole project"}</span>}
              </button>
            );
          })}
        </div>
        {/* Mirrors the Tech reviewer menu's own "Not required (NA)" row, so the
            two role columns are marked the same way. Offered only when nobody
            is assigned — with a proofer on the job the question is settled. */}
        {na && !na.value && rows.length === 0 && (
          <button className="ap-rbtn ap-na" onClick={() => { na.set(true); onDone?.(); }}>
            <span className="ap-av sm empty">–</span>
            <span className="ap-rnm">Not required</span>
            <span className="ap-has">no proofer needed</span>
          </button>
        )}
        {blocked && <div className="ap-cta">Tick at least one file to assign a subset.</div>}
      </div>
    </>
  );
}

/** The Tracking View's home for the panel — same interaction, and now the same
 *  section framing as Chapters × modules and Essentials: one SectionHead above
 *  a plain white card, rather than a mono-uppercase title inside a grey one. */
export function AssignProofer({ projectId, workflowId, files }: {
  projectId: string; workflowId: string; files: TrackFile[];
}) {
  // Deduped by React Query against the panel's own read — one request.
  const { data: assignments } = useProjectAssignments(projectId);
  const { list, updateWorkflow } = useWorkflowBoard();
  const wf = list.find((w) => w.id === workflowId);
  const n = (assignments || []).length;
  const na = {
    value: wf?.proofer === "NA",
    set: (on: boolean) => updateWorkflow(workflowId, { proofer: on ? "NA" : null }),
  };
  return (
    <div className="tv-stick tv-assignsec">
      <SectionHead
        title="Proofer assignments"
        sub="Who's proofing what."
        right={<span className="sec-count">{n ? `${n} assigned` : na.value ? "Not required" : "None assigned"}</span>}
      />
      <div className="ap ap-card">
        <AssignProoferPanel projectId={projectId} files={files} na={na} />
      </div>
    </div>
  );
}

function RosterMenu({ roster, onPick }: { roster: Member[]; onPick: (id: string) => void }) {
  return (
    <div className="ap-menu">
      {roster.map((m) => (
        <button key={m.id} onClick={() => onPick(m.id)}>
          <span className="ap-av xs" style={{ background: avatarColor(m.name || m.email) }}>
            {initials(m.name || m.email)}
          </span>
          {m.name || m.email}
        </button>
      ))}
    </div>
  );
}
